import { NgTemplateOutlet } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    inject,
    type OnDestroy,
    type OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from '@openng/optimus-ui/button';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { isNil } from 'lodash-es';

import { getTourDefinition } from '@app/modules/learning-hub/constants/tour-registry';
import {
    LearningHubService,
    type LearningTourAdapter,
    type WebinarAdapter,
} from '@app/modules/learning-hub/services/learning-hub.service';
import { TourService } from '@app/modules/learning-hub/services/tour.service';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { AttachmentService } from '@app/shared/services/attachment.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

type TourState = 'done' | 'new' | 'soon';

export interface TourRow extends LearningTourAdapter {
    titleNum: string;
    titleName: string;
    sub: string;
    subHeading: boolean;
    state: TourState;
    minutes: number | null;
    steps: number | null;
}

interface TourTrack {
    name: string;
    rows: TourRow[];
    done: number;
    available: number;
    minutes: number;
}

@Component({
    selector: 'cor-learning-hub',
    templateUrl: './learning-hub.component.html',
    styleUrls: ['./learning-hub.component.scss'],
    standalone: true,
    imports: [ButtonModule, SkeletonModule, NoDataComponent, TooltipModule, NgTemplateOutlet],
})
export class LearningHubComponent implements OnInit, OnDestroy {
    private static readonly TRACK_COLOURS = [
        'var(--p-slate-500)',
        'var(--p-amber-600)',
        'var(--p-teal-600)',
    ];

    private readonly appConfigService = inject(AppConfigService);
    private readonly toast = inject(ToastNotificationService);
    private readonly learningHubService = inject(LearningHubService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly attachmentService = inject(AttachmentService);
    private readonly tourService = inject(TourService);

    protected readonly tours = {
        productFruitsId: computed(() => this.appConfigService.appConfig().productFruits),
        tourList: signal<LearningTourAdapter[]>(null),
        completedIds: signal<Set<number>>(new Set()),
        tracks: computed((): TourTrack[] => {
            const list = this.tours.tourList();
            if (!list || list.length === 0) return [];

            const pfTours = this.learningHubService.productFruitsIsReady()
                ? ((window.productFruits?.api?.tours?.getTours() as {
                      name: string;
                      id: number;
                      userState: 'skipped' | 'finished';
                  }[]) ?? [])
                : [];
            const completed = this.tours.completedIds();

            const rows = list.map((tour): TourRow => {
                const bracket = tour.title.indexOf(']');
                const definition = tour.key ? getTourDefinition(tour.key) : undefined;
                const finished =
                    completed.has(tour.id) ||
                    pfTours.find(x => x.name === tour.title)?.userState === 'finished';
                const parts = tour.group?.split('.') ?? [];
                return {
                    ...tour,
                    titleNum: bracket >= 0 ? tour.title.slice(1, bracket) : '',
                    titleName: bracket >= 0 ? tour.title.slice(bracket + 1).trim() : tour.title,
                    sub: parts.length > 1 ? parts.slice(1).join('.') : '',
                    subHeading: false,
                    state: !tour.key ? 'soon' : finished ? 'done' : 'new',
                    minutes: definition?.estimatedMinutes ?? null,
                    steps: definition ? definition.steps.length + (definition.intro ? 1 : 0) : null,
                    userState: finished ? 'finished' : tour.userState,
                };
            });

            const byTrack = new Map<string, TourRow[]>();
            for (const row of rows) {
                const name = row.group?.split('.')[0] || 'Tours';
                const bucket = byTrack.get(name) ?? [];
                bucket.push(row);
                byTrack.set(name, bucket);
            }
            return [...byTrack.entries()]
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, bucket]) => {
                    const sorted = [...bucket].sort(
                        (a, b) => a.sub.localeCompare(b.sub) || a.sortOrder - b.sortOrder,
                    );
                    sorted.forEach((row, k) => {
                        row.subHeading =
                            Boolean(row.sub) && (k === 0 || sorted[k - 1].sub !== row.sub);
                    });
                    const available = sorted.filter(row => row.state !== 'soon');
                    return {
                        name,
                        rows: sorted,
                        done: available.filter(row => row.state === 'done').length,
                        available: available.length,
                        minutes: available.reduce((sum, row) => sum + (row.minutes ?? 0), 0),
                    };
                });
        }),
        progress: computed((): { done: number; available: number; pct: number } => {
            const tracks: TourTrack[] = this.tours.tracks();
            const done = tracks.reduce((sum, t) => sum + t.done, 0);
            const available = tracks.reduce((sum, t) => sum + t.available, 0);
            return { done, available, pct: available ? Math.round((done / available) * 100) : 0 };
        }),
        upNext: computed((): TourRow | undefined => {
            const pending = this.tours
                .tracks()
                .flatMap((track: TourTrack) => track.rows)
                .filter((row: TourRow) => row.state === 'new');
            return pending.find((row: TourRow) => row.minutes !== null) ?? pending[0];
        }),
        // Only the database call gates loading; a tour provider is not required to list tours.
        isLoading: computed((): boolean => isNil(this.tours.tourList())),
        error: signal<string | null>(null),
        hasError: computed((): boolean => this.tours.error() !== null),
        completionEventListener: null as any,
    };

    private readonly selectedTrack = signal<string | null>(null);

    protected readonly showWebinars = signal(false);

    protected readonly query = signal('');

    protected readonly activeTrack = computed((): TourTrack | undefined => {
        const tracks = this.tours.tracks();
        const chosen = this.selectedTrack();
        if (chosen) return tracks.find(t => t.name === chosen) ?? tracks[0];
        const next = this.tours.upNext();
        return (next && tracks.find(t => t.rows.includes(next))) ?? tracks[0];
    });

    protected readonly searchResults = computed((): TourRow[] => {
        const q = this.query().trim().toLowerCase();
        if (!q) return [];
        return this.tours
            .tracks()
            .flatMap((t: TourTrack) => t.rows)
            .filter(
                (row: TourRow) =>
                    row.title.toLowerCase().includes(q) ||
                    (row.description ?? '').toLowerCase().includes(q) ||
                    (row.group ?? '').toLowerCase().includes(q),
            );
    });

    protected readonly webinars = {
        webinarList: signal<WebinarAdapter[]>([]),
        thumbnails: signal<Record<number, string>>({}),
        isLoading: signal(false),
    };

    ngOnInit() {
        this.loadLearningTours();
        this.loadWebinars();
        this.initializeTourCompletionTracking();
    }

    // Tours
    protected loadLearningTours() {
        this.learningHubService
            .getToursList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => this.tours.tourList.set(result));
        this.learningHubService
            .getCompletedTours()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ result }) => this.tours.completedIds.set(new Set(result.tourIds)),
                error: () => undefined,
            });
    }

    protected selectTrack(track: TourTrack) {
        this.selectedTrack.set(track.name);
        this.showWebinars.set(false);
        this.query.set('');
    }

    protected selectWebinars() {
        this.showWebinars.set(true);
        this.query.set('');
    }

    protected trackBlurb(track: TourTrack): string {
        const blurbs: Record<string, string> = {
            'Getting Started': 'Find your way around GGX and see where your work lives.',
            'How To Guides':
                'Hands-on walkthroughs. You do the clicks, the coach explains what each one means.',
            'Learn Risk in AI':
                'Why each control exists, shown on the real chatbot. Short reads with one or two clicks.',
        };
        return blurbs[track.name] ?? '';
    }

    protected isFinished(track: TourTrack): boolean {
        return track.available > 0 && track.done >= track.available;
    }

    protected trackColour(track: TourTrack | undefined): string {
        if (!track) return 'transparent';
        if (this.isFinished(track)) return 'var(--p-green-600)';
        const index = this.tours.tracks().indexOf(track);
        return LearningHubComponent.TRACK_COLOURS[
            index % LearningHubComponent.TRACK_COLOURS.length
        ];
    }

    protected ringStyle(track: TourTrack, index: number): string {
        if (this.isFinished(track)) return 'var(--p-green-600)';
        const colours = LearningHubComponent.TRACK_COLOURS;
        const colour = colours[index % colours.length];
        const deg = track.available ? Math.round((track.done / track.available) * 360) : 0;
        return `conic-gradient(${colour} ${deg}deg, var(--p-content-border-color) 0)`;
    }

    protected minutesLeft(track: TourTrack): number {
        return track.rows
            .filter(row => row.state === 'new')
            .reduce((sum, row) => sum + (row.minutes ?? 0), 0);
    }

    protected subStats(track: TourTrack, sub: string): { done: number; total: number } {
        const rows = track.rows.filter(row => row.sub === sub && row.state !== 'soon');
        return { done: rows.filter(row => row.state === 'done').length, total: rows.length };
    }

    protected startTour(tour: LearningTourAdapter) {
        // Code-owned tours take precedence; Product Fruits remains the fallback until
        // every tour has been migrated.
        if (this.tourService.hasDefinition(tour.key)) {
            this.startCodeOwnedTour(tour);
            return;
        }

        if (!this.tours.productFruitsId()) {
            this.toast.showError('Help Center is not configured');
            return;
        }

        if (!window.productFruitsIsReady) {
            this.toast.showWarning(
                'Help Center is not ready yet - please try again after sometime',
            );
            return;
        }

        const productFruitTour = (
            window.productFruits.api.tours.getTours() as { name: string; id: number }[]
        )?.find(x => x.name === tour.title);
        if (!productFruitTour) {
            this.toast.showWarning(
                'Help Center is not configured correctly - please contact your admin',
            );
            return;
        }

        try {
            window.productFruits.api.tours.tryStartTour(productFruitTour.id);
            this.learningHubService.closeDialog();
            // Store the tour start time in the backend
            this.learningHubService
                .trackTourStarted(productFruitTour.id, tour.title)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to start tour. Please try again.';
            this.toast.showError(errorMessage);
            console.error('Error starting tour:', error);
        }
    }

    /**
     * Launches a tour from the code-owned registry.
     *
     * The tour service owns the whole run, including start and finish tracking against
     * the GGX `LearningTour.id`, so `tour_tracking` history and Slack notifications
     * continue to work unchanged.
     */
    private startCodeOwnedTour(tour: LearningTourAdapter) {
        try {
            if (!this.tourService.launchTour(tour)) {
                this.toast.showWarning('This tour is not available yet');
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to start tour. Please try again.';
            this.toast.showError(errorMessage);
            console.error('Error starting tour:', error);
        }
    }

    private initializeTourCompletionTracking() {
        // Set up the listener to track when tours are completed
        if (
            this.learningHubService.productFruitsIsReady() &&
            window.productFruits?.api?.tours &&
            !this.tours.completionEventListener
        ) {
            const callback: any = (tourId: number) => {
                // Find the tour by Product Fruits ID
                const pfTourList = window.productFruits.api.tours.getTours() as {
                    name: string;
                    id: number;
                }[];
                const pfTour = pfTourList?.find(x => x.id === tourId);
                if (!pfTour) return;
                // Store the tour end time in the backend
                this.learningHubService
                    .trackTourFinished(tourId, pfTour.name)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe();
            };
            this.tours.completionEventListener = window.productFruits.api.tours.listen(
                'tour-finished',
                callback,
            );
        }
    }

    ngOnDestroy() {
        // Clean up event listener
        if (this.tours.completionEventListener) {
            this.tours.completionEventListener(); // This tells Product Fruits to stop listening for the event
            this.tours.completionEventListener = null;
        }
    }

    protected refreshTours() {
        this.loadLearningTours();
    }

    // Webinars
    protected loadWebinars() {
        this.webinars.isLoading.set(true);
        this.learningHubService
            .getWebinarsList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                this.webinars.webinarList.set(result);
                this.loadThumbnails(result);
            })
            .add(() => this.webinars.isLoading.set(false));
    }

    private loadThumbnails(webinars: WebinarAdapter[]) {
        for (const webinar of webinars) {
            if (!webinar.link?.includes('vimeo.com')) continue;
            const video = encodeURIComponent(webinar.link.split('?')[0]);
            fetch(`https://vimeo.com/api/oembed.json?url=${video}`)
                .then(response => (response.ok ? response.json() : null))
                .then((data: { thumbnail_url?: string } | null): void => {
                    const thumbnail = data?.thumbnail_url;
                    if (!thumbnail) return;
                    this.webinars.thumbnails.update(all => ({ ...all, [webinar.id]: thumbnail }));
                })
                .catch((): void => undefined);
        }
    }

    protected openWebinarLink(webinar: WebinarAdapter) {
        window.open(webinar.link, '_blank');
    }

    protected downloadWebinarPDF(webinar: WebinarAdapter) {
        this.attachmentService.saveAs(webinar.fileId);
    }
}
